/**
 * Modulo di logging strutturato per il flusso di login MSAL.
 * Fornisce funzioni per tracciare le varie fasi del processo di autenticazione.
 */

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogContext {
  timestamp: string;
  phase: string;
  accountId?: string;
  consumerRef?: string;
  [key: string]: unknown;
}

const LOG_PREFIX = '[MSAL Login]';

/**
 * Crea un contesto di log con timestamp ISO e informazioni di base.
 */
const createLogContext = (
  phase: string,
  additionalData?: Record<string, unknown>,
): LogContext => ({
  timestamp: new Date().toISOString(),
  phase,
  ...additionalData,
});

/**
 * Esegue il log formattato in console.
 */
const log = (level: LogLevel, message: string, context?: LogContext) => {
  const formattedMessage = `${LOG_PREFIX} ${message}`;

  switch (level) {
    case 'info':
      console.log(formattedMessage, context || '');
      break;
    case 'warn':
      console.warn(formattedMessage, context || '');
      break;
    case 'error':
      console.error(formattedMessage, context || '');
      break;
    case 'debug':
      if (import.meta.env.DEV) {
        console.debug(formattedMessage, context || '');
      }
      break;
  }
};

/**
 * Logga una fase del flusso di login.
 * @param phase - Nome della fase (es. 'INIT', 'REDIRECT_HANDLING', 'TOKEN_ACQUISITION')
 * @param data - Dati aggiuntivi da loggare
 */
export const logLoginFlow = (
  phase: string,
  data?: Record<string, unknown>,
): void => {
  const context = createLogContext(phase, data);
  log('info', `Flow phase: ${phase}`, context);
};

/**
 * Logga l'acquisizione del token.
 * @param success - Se l'acquisizione ha avuto successo
 * @param data - Dati aggiuntivi (es. scopes, account info)
 * @param error - Errore in caso di fallimento
 */
export const logTokenAcquisition = (
  success: boolean,
  data?: Record<string, unknown>,
  error?: unknown,
): void => {
  const context = createLogContext('TOKEN_ACQUISITION', {
    success,
    ...data,
    ...(error && {
      error: error instanceof Error ? error.message : String(error),
    }),
  });

  if (success) {
    log('info', 'Token acquired successfully', context);
  } else {
    log('error', 'Token acquisition failed', context);
  }
};

/**
 * Logga la gestione del redirect da B2C.
 * @param response - Risposta dal redirect (può essere null)
 * @param accountSet - Se l'account è stato impostato come attivo
 */
export const logRedirectHandling = (
  response: { account?: { homeAccountId?: string } | null } | null,
  accountSet: boolean,
): void => {
  const context = createLogContext('REDIRECT_HANDLING', {
    hasResponse: !!response,
    hasAccount: !!response?.account,
    accountId: response?.account?.homeAccountId,
    accountSet,
  });

  if (response?.account) {
    log('info', 'Redirect handled with account', context);
  } else {
    log('warn', 'Redirect handled without account', context);
  }
};

/**
 * Logga un errore con contesto.
 * @param errorContext - Contesto in cui si è verificato l'errore
 * @param error - L'errore catturato
 * @param additionalData - Dati aggiuntivi utili per il debug
 */
export const logError = (
  errorContext: string,
  error: unknown,
  additionalData?: Record<string, unknown>,
): void => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorName = error instanceof Error ? error.name : 'Unknown';

  const context = createLogContext('ERROR', {
    errorContext,
    errorName,
    errorMessage,
    ...additionalData,
  });

  log('error', `Error in ${errorContext}: ${errorMessage}`, context);
};

/**
 * Logga un tentativo di retry.
 * @param attempt - Numero del tentativo corrente
 * @param maxAttempts - Numero massimo di tentativi
 * @param reason - Motivo del retry
 */
export const logRetry = (
  attempt: number,
  maxAttempts: number,
  reason: string,
): void => {
  const context = createLogContext('RETRY', {
    attempt,
    maxAttempts,
    reason,
  });

  log('warn', `Retry attempt ${attempt}/${maxAttempts}: ${reason}`, context);
};

/**
 * Logga lo stato dell'account MSAL.
 * @param account - Account attivo o null
 * @param source - Dove è stato ottenuto l'account (es. 'getActiveAccount', 'handleRedirectPromise')
 */
export const logAccountState = (
  account: { homeAccountId?: string; username?: string } | null,
  source: string,
): void => {
  const context = createLogContext('ACCOUNT_STATE', {
    hasAccount: !!account,
    accountId: account?.homeAccountId,
    username: account?.username,
    source,
  });

  if (account) {
    log('debug', `Account found from ${source}`, context);
  } else {
    log('debug', `No account from ${source}`, context);
  }
};

/**
 * Logga il valore del consumerRef salvato/recuperato.
 * @param consumerRef - Valore del consumerRef
 * @param action - 'saved' | 'retrieved' | 'missing'
 */
export const logConsumerRef = (
  consumerRef: string | null | undefined,
  action: 'saved' | 'retrieved' | 'missing',
): void => {
  const context = createLogContext('CONSUMER_REF', {
    consumerRef: consumerRef || '(empty)',
    action,
  });

  if (action === 'missing' || !consumerRef) {
    log('warn', `ConsumerRef ${action}`, context);
  } else {
    log('debug', `ConsumerRef ${action}: ${consumerRef}`, context);
  }
};

/**
 * Logga eventi di autenticazione per debug diagnostico.
 * Attivo SOLO se localStorage['debug.auth'] === '1'.
 * Usato per tracciare il flusso reale di login/logout in ambiente test.
 */
export const logAuthTrace = (
  event: string,
  data?: Record<string, unknown>,
): void => {
  if (localStorage.getItem('debug.auth') !== '1') return;
  console.info(
    '[AUTH-TRACE]',
    JSON.stringify({ event, ts: Date.now(), ...data }),
  );
};
